// src/features/monetize/components/ScopePicker.tsx
//
// What a product covers: everything the account publishes, or a set of
// playlists and single items. Playlists are few and mount whole; media is
// searched over a bounded option list (CLAUDE.md: bounded combobox data).

import { useMemo, useState } from 'react';
import { Group, MultiSelect, Pill, Select, Stack, Switch, Text } from '@mantine/core';
import { usePlaylists } from '@/features/playlists/usePlaylists';
import { useVideos } from '@/features/media/useVideos';
import { useStreams } from '@/features/streams/useStreams';
import { LIST_TYPE_LABELS } from '@/lib/constants/playlist';
import { MONETIZE_CONSTRAINTS } from '@/lib/constants/monetize';
import { MonetizeUtils, type MediaCandidate } from '../MonetizeUtils';

export interface ScopeLookups {
  lists: Map<number, string>;
  media: Map<number, string>;
}

/** The titles behind scope tokens, from the playlists, videos and streams already loaded. */
export function useScopeLookups(): { lookups: ScopeLookups; candidates: MediaCandidate[]; listOptions: { value: string; label: string }[]; isLoading: boolean } {
  const playlists = usePlaylists();
  const { videos, isLoading: videosLoading } = useVideos({ refetchOnMount: false });
  const { streams, isLoading: streamsLoading } = useStreams({ refetchOnMount: false });

  return useMemo(() => {
    const lists = new Map<number, string>();
    const listOptions: { value: string; label: string }[] = [];
    if (playlists.data) {
      for (const p of [...playlists.data.customLists, ...playlists.data.tagLists]) {
        lists.set(p.listId, p.title);
        listOptions.push({ value: MonetizeUtils.scopeToken('list', p.listId), label: `${p.title} (${LIST_TYPE_LABELS[p.listType]})` });
      }
      listOptions.sort((a, b) => a.label.localeCompare(b.label));
    }
    const media = new Map<number, string>();
    const candidates: MediaCandidate[] = [];
    for (const v of videos) {
      media.set(v.media.mediaId, v.media.title);
      candidates.push({ id: v.media.mediaId, title: v.media.title, titleLower: v.media.title.toLowerCase(), live: false });
    }
    for (const s of streams) {
      media.set(s.media.mediaId, s.media.title);
      candidates.push({ id: s.media.mediaId, title: s.media.title, titleLower: s.media.title.toLowerCase(), live: true });
    }
    return { lookups: { lists, media }, candidates, listOptions, isLoading: playlists.isLoading || videosLoading || streamsLoading };
  }, [playlists.data, playlists.isLoading, videos, streams, videosLoading, streamsLoading]);
}

interface ScopePickerProps {
  value: string[];
  onChange: (scopes: string[]) => void;
  wholeService: boolean;
  onWholeServiceChange: (whole: boolean) => void;
  error?: string;
  /** Hide the whole-service switch (a pass is always about particular content). */
  allowWholeService?: boolean;
}

export function ScopePicker({ value, onChange, wholeService, onWholeServiceChange, error, allowWholeService = true }: ScopePickerProps) {
  const { lookups, candidates, listOptions } = useScopeLookups();
  const [search, setSearch] = useState('');

  const listValues = value.filter((s) => s.startsWith('list:'));
  const mediaValues = value.filter((s) => s.startsWith('media:'));
  const mediaIds = useMemo(() => new Set(mediaValues.map((s) => MonetizeUtils.parseScope(s)?.id ?? 0)), [mediaValues]);
  const mediaOptions = useMemo(
    () => MonetizeUtils.searchMedia(candidates, mediaIds, search, MONETIZE_CONSTRAINTS.MEDIA_SEARCH_MAX_OPTIONS),
    [candidates, mediaIds, search],
  );

  return (
    <Stack gap="sm">
      {allowWholeService && (
        <Switch
          label="Everything you publish"
          description="One purchase covers every video and live stream in this account."
          checked={wholeService}
          onChange={(e) => onWholeServiceChange(e.currentTarget.checked)}
        />
      )}
      {!wholeService && (
        <>
          <MultiSelect
            label="Playlists"
            description="Everything in these playlists, now and later."
            data={listOptions}
            value={listValues}
            onChange={(lists) => onChange([...lists, ...mediaValues])}
            searchable
            clearable
            placeholder={listValues.length === 0 ? 'Pick playlists' : undefined}
            error={error}
          />
          <Select
            label="Single videos or live streams"
            description="Search by title and pick one at a time."
            placeholder="Type to search"
            data={mediaOptions}
            searchable
            searchValue={search}
            onSearchChange={setSearch}
            filter={({ options }) => options}
            nothingFoundMessage={search ? 'No match' : 'Type to search'}
            value={null}
            onChange={(id) => {
              if (id) onChange([...listValues, ...mediaValues, MonetizeUtils.scopeToken('media', Number(id))]);
              setSearch('');
            }}
          />
          {mediaValues.length > 0 && (
            <Group gap="xs">
              <Text size="sm" c="dimmed">Items:</Text>
              <Pill.Group>
                {mediaValues.map((token) => (
                  <Pill key={token} withRemoveButton onRemove={() => onChange(value.filter((s) => s !== token))}>
                    {MonetizeUtils.scopeLabel(token, lookups)}
                  </Pill>
                ))}
              </Pill.Group>
            </Group>
          )}
        </>
      )}
    </Stack>
  );
}
